import showMessage from '@/layouts/components/Message/index'
import { useUserStoreHook } from '@/store/modules/user'
import { deepTrimObjectValues, safeTrimObjectValues } from '@/utils/data'
import IndexDB from '@/utils/indexdb'
import { isObject } from '@/utils/validate'
import axios, { type AxiosInstance, type AxiosRequestConfig, type AxiosResponse } from 'axios'
import { ElMessage } from 'element-plus'
import { get, merge } from 'lodash-es'
// @ts-ignore
import qs from 'qs'
import { getToken } from './cookies'
// 方法
import { BAD_REQUEST_TIP } from './service-tips'
// 国际化
import i18n, { t } from '@/locales/index'

// 环境常量
const BASE_API = import.meta.env.VITE_BASE_API
const VITE_APP_ENV = import.meta.env.VITE_USER_NODE_ENV

// 語言與請求頭 Language 的映射（簡體 zh-cn / 繁體 zh-tw / 英文 en-us）
const langToHeaderMap: Record<string, string> = {
  'zh-HK': 'zh-tw',
  'zh-CN': 'zh-cn',
  'en-US': 'en-us',
}
// 獲取當前語言的請求頭值（每次請求實時讀取當前語言）
const getLanguageHeader = (): string => {
  const locale: string = i18n.global.locale.value as string
  return langToHeaderMap[locale] || 'zh-tw'
}
// 全部選項
const allClinicItem: Record<string, any> = {
  clinicCode: 'tcma-self-all',
  enName: 'All',
  name: '全部',
  clinicName: '全部',
  clinicEnName: '全部',
}
// HTTP状态码
const HTTP_STATUS = {
  OK: 200,
  NO_CONTENT: 204,
  BAD_REQUEST: 400,
  UNAUTHORIZED: 401,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  REQUEST_TIMEOUT: 408,
  INTERNAL_SERVER_ERROR: 500,
  NOT_IMPLEMENTED: 501,
  BAD_GATEWAY: 502,
  SERVICE_UNAVAILABLE: 503,
  GATEWAY_TIMEOUT: 504,
  HTTP_VERSION_NOT_SUPPORTED: 505,
  TOKEN_EXPIRED: 901,
  ACCOUNT_LOGIN_OTHER_PLACE: 902,
} as const

// Content-Type常量
const CONTENT_TYPE = {
  JSON: 'application/json',
  FORM_URLENCODED: 'application/x-www-form-urlencoded',
  PROBLEM_JSON: 'application/problem+json; charset=utf-8',
} as const

// 请求选项类型扩展
interface RequestOptions extends Record<string, any> {
  /** 是否开启缓存（优先读取缓存） */
  cache?: boolean
  /** 是否写入缓存（请求成功后缓存） */
  useCache?: boolean
  /** 是否禁用超时（0为不设置超时） */
  unTimeOut?: boolean
  /** 参数去空格类型：safe(浅) | deep(深) */
  trimType?: 'safe' | 'deep'
  /** 响应类型 */
  responseType?: AxiosRequestConfig['responseType']
  /** 是否在返回数组首位加入“全部”项 */
  hasAll?: boolean
  /** 开发环境覆盖 baseURL，仅在 VITE_APP_ENV === 'development' 时生效 */
  baseURL?: string
}

// 扩展Axios配置类型 (用于请求/响应拦截器)
interface CustomAxiosRequestConfig extends AxiosRequestConfig {
  cache?: boolean
  useCache?: boolean
}

// ===================== 核心工具函数 =====================
/**
 * 退出登录并刷新页面
 */
const logout = (): void => {
  useUserStoreHook().logout()
  // location.reload() // 如需刷新可取消注释
}

/**
 * 获取当日零点时间戳（缓存有效期基准）
 */
const getTodayZeroTime = (): number => {
  return new Date(new Date().setHours(0, 0, 0, 0)).getTime()
}

/**
 * 生成缓存Key（URL + 参数序列化 + 当前语言）
 * 接口 handle 字段按请求头 Language 返回，缓存Key须携带语言，避免跨语言命中旧语言缓存
 */
const generateCacheKey = (url: string, params?: Record<string, any>): string => {
  const lang = getLanguageHeader()
  if (!params) return `${url}?lang=${lang}`
  return `${url}?${qs.stringify(params)}&lang=${lang}`
}

/**
 * 缓存读取逻辑
 */
const getCacheData = async (url: string, params?: Record<string, any>): Promise<any> => {
  const key = generateCacheKey(url, params)
  const todayZeroTime = getTodayZeroTime()

  try {
    const cacheData = await IndexDB.get(key)
    if (!cacheData?.content) return Promise.reject()

    const cacheContent = JSON.parse(cacheData.content)
    // 校验缓存有效性：当日缓存 + 有有效数据
    const isCacheValid =
      cacheContent.tt === todayZeroTime &&
      cacheContent.data &&
      (Array.isArray(cacheContent.data) ? cacheContent.data.length : Object.keys(cacheContent.data).length)

    if (isCacheValid) {
      return cacheContent.data
    } else {
      await IndexDB.remove(key)
      return Promise.reject()
    }
  } catch (error) {
    return Promise.reject()
  }
}

/**
 * 缓存写入逻辑
 */
const setCacheData = async (url: string, params?: Record<string, any>, data?: any): Promise<void> => {
  const key = generateCacheKey(url, params)
  const todayZeroTime = getTodayZeroTime()
  await IndexDB.add(key, JSON.stringify({ tt: todayZeroTime, data }))
}

/**
 * 参数去空格处理
 */
const trimParams = (data: any, trimType: 'safe' | 'deep' = 'safe'): any => {
  if (Array.isArray(data)) return data
  if (!isObject(data)) return data

  return trimType === 'deep' ? deepTrimObjectValues(data) : safeTrimObjectValues(data)
}

/**
 * 处理POST/PUT请求的Content-Type和参数序列化
 */
const handlePostPutConfig = (
  method: string,
  params: Record<string, any>,
  paramsComplexity: 'simple' | 'complex' = 'simple', // 接口複雜度 complex是多個參數但可以只傳一個參數
): { contentType: string; data: any } => {
  const isPost = method.toUpperCase() === 'POST'
  const isPut = method.toUpperCase() === 'PUT'

  // if (!isPost && !isPut) {
  //   return {
  //     contentType: CONTENT_TYPE.JSON,
  //     data: params,
  //   }
  // }

  // 核心逻辑：自动判断请求类型
  // 1. 如果有单个参数，使用 JSON (Multi-parameter)
  // 2. 如果是多个参数，但参数值是复杂类型（对象/数组），使用 JSON
  // 3. 这里的 keys.length === 1 或参数值是复杂类型，等价于之前的 requestType === 'Multi-parameter-post-request'
  // 4. 區別在於，post請求接受一個參數時，區分為固定一個參數、多個參數但某一次是一個參數
  const keys = Object.keys(params)
  const isComplexValue = Object.values(params).some((val) => isObject(val) || Array.isArray(val))
  const isSingleParam = isObject(params) && keys.length === 1 && !isComplexValue

  // 自动判定为 Multi-parameter-post-request 的情况：
  // 满足注释中的 1 (单个参数) 或 2 (多个参数且含复杂值)
  const isMultiParamPost = keys.length === 1 || isComplexValue
  console.log('isMultiParamPost == ', isMultiParamPost)
  console.log('isSingleParam == ', isSingleParam)
  console.log('isComplexValue == ', isComplexValue)
  console.log('isPost == ', isPost)
  console.log('isPut == ', isPut)
  if (isSingleParam && (isPost || isPut) && paramsComplexity === 'simple') {
    if (isPost && !isMultiParamPost) {
      return {
        contentType: CONTENT_TYPE.FORM_URLENCODED,
        data: qs.stringify(params),
      }
    }
    return {
      contentType: CONTENT_TYPE.FORM_URLENCODED,
      data: params,
    }
  }
  return {
    contentType: CONTENT_TYPE.JSON,
    data: params,
  }
  // POST 请求逻辑
  // if (isPost) {
  //   // 如果满足多参数或复杂值条件，使用 JSON；否则使用表单编码
  //   if (isMultiParamPost) {
  //     return {
  //       contentType: CONTENT_TYPE.FORM_URLENCODED,
  //       data: qs.stringify(params),
  //     }
  //   }
  //   return {
  //     // contentType: CONTENT_TYPE.FORM_URLENCODED,
  //     // data: qs.stringify(params),
  //     contentType: CONTENT_TYPE.JSON,
  //     data: params,
  //   }
  // }

  // // PUT 请求逻辑 (保持原样：单参数表单编码，多参数 JSON)
  // if (isPut && isSingleParam) {
  //   return {
  //     contentType: CONTENT_TYPE.FORM_URLENCODED,
  //     data: params,
  //   }
  // }

  // // 默认 JSON 编码
  // return {
  //   contentType: CONTENT_TYPE.JSON,
  //   data: params,
  // }
}

/**
 * 响应结果处理
 */
const handleResponse = (response: AxiosResponse): any => {
  const { config, data: apiData, status, headers } = response
  const { useCache } = config as CustomAxiosRequestConfig
  // console.log('useCache response == ', useCache, config, response)
  // 处理缓存写入
  if (useCache) {
    const params = config.method?.toUpperCase() === 'GET' ? config.params : config.data
    setCacheData(config.url!, params, apiData).catch(() => {})
  }

  // 二进制数据（文件下载）处理
  if (['blob', 'arraybuffer'].includes(config.responseType!)) {
    // 解析下载文件名
    if (headers['content-disposition']) {
      const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/
      const matches = filenameRegex.exec(headers['content-disposition'])
      if (matches?.[1]) {
        apiData.filename = decodeURIComponent(matches[1].replace(/['"]/g, ''))
      }
    }
    return apiData
  }

  // 非本系统接口（无状态码）
  if (status === undefined) {
    ElMessage.error(t('Tips_Common.non_system_interface'))
    return Promise.reject(new Error(t('Tips_Common.non_system_interface')))
  }

  // 业务状态码处理
  switch (status) {
    case HTTP_STATUS.OK:
      return apiData
    case HTTP_STATUS.NO_CONTENT:
      return
    case HTTP_STATUS.UNAUTHORIZED:
      logout()
      return Promise.reject(new Error(t('Tips_Common.token_expired')))
    default:
      console.log('apiData ==', apiData)
      ElMessage.error(apiData.message || t('Tips_Common.request_error'))
      return Promise.reject(new Error(apiData.message || t('Tips_Common.request_error')))
  }
}

/**
 * 错误处理
 */
const handleError = (error: any): Promise<never> => {
  const status = get(error, 'response.status')
  // const businessErrorType = error?.response?.data?.businessErrorType
  const errorMsg = error?.response?.data?.message

  // 状态码对应提示
  switch (status) {
    case HTTP_STATUS.BAD_REQUEST:
      error.message = BAD_REQUEST_TIP(error, t) || errorMsg || t('Tips_Common.bad_request')
      break
    case HTTP_STATUS.UNAUTHORIZED:
      error.message = t('Tips_Common.relogin')
      logout()
      break
    case HTTP_STATUS.NOT_FOUND:
      error.message = `${t('Tips_Common.request_404')} - ${error?.config?.url}`
      break
    case HTTP_STATUS.REQUEST_TIMEOUT:
      error.message = t('Tips_Common.request_timeout')
      break
    case HTTP_STATUS.BAD_GATEWAY:
      error.message = t('Tips_Common.system_unavailable')
      break
    case HTTP_STATUS.TOKEN_EXPIRED:
      error.message = t('Tips_Common.login_invalid')
      logout()
      break
    case HTTP_STATUS.ACCOUNT_LOGIN_OTHER_PLACE:
      error.message = t('Tips_Common.account_other_login')
      logout()
      break
    // 其他状态码可按需补充
    default:
      error.message = error.message || t('Tips_Common.unknown_error')
  }

  // 网络错误处理
  if (!error?.response && error?.code === 'ERR_NETWORK') {
    error.message = t('Tips_Common.err_network')
  }

  // 统一错误提示
  if (error.message) {
    showMessage({ message: error.message, type: 'error' })
  }

  return Promise.reject(error)
}

// ===================== Axios实例创建 =====================
/**
 * 创建Axios实例
 */
const createAxiosInstance = (): AxiosInstance => {
  const service = axios.create({
    baseURL: BASE_API,
    timeout: 30000,
    headers: {
      post: { 'Content-Type': CONTENT_TYPE.JSON },
      put: { 'Content-Type': CONTENT_TYPE.FORM_URLENCODED },
    },
  })

  // 请求拦截器
  service.interceptors.request.use(
    (config: any) => {
      const token = getToken()
      console.log('getToken token == ', token, config)
      // 設置語言請求頭（繁體 zh-tw / 英文 en-us），實時讀取當前語言
      config.headers.Language = getLanguageHeader()
      // 携带Token
      if (token) {
        config.headers.Authorization = `Bearer ${token}`
      }
      // 开发环境添加Domain头 组织：'test.hknongs.cn' 诊所：'test.test.hknongs.cn' 平台：'hknongs.cn'
      if (VITE_APP_ENV === 'development') {
        //   // config.headers.Domain = 'test.hknongs.cn'
        // const VITE_BASE_URL = axiosCreateOption?.baseURL
        const dev_portal = localStorage.getItem('dev_portal') || '0'
        const dev_url = localStorage.getItem('dev_url') || '0'
        console.log('dev_portal == ', dev_portal)
        if (['2', 2].includes(dev_portal)) {
          config.headers.Domain = `${dev_url}.tcma.hknongs.cn`
        } else {
          config.headers.Domain = 'tcma.hknongs.cn'
        }
        //   // const environment = localStorage.getItem('dev_url')
        //   // if (VITE_BASE_URL) {
        //   //   const url = new URL(VITE_BASE_URL)
        //   //   let hostname = url.hostname
        //   //   if (environment) {
        //   //     const parts = hostname.split('.')
        //   //     parts[0] = environment
        //   //     hostname = parts.join('.')
        //   //   }
        //   //   config.headers.Domain = `${url.protocol}//${hostname}`
        //   // }
      }
      return config
    },
    (error) => Promise.reject(error),
  )

  // 响应拦截器
  service.interceptors.response.use(handleResponse, handleError)

  return service
}

// ===================== 请求方法封装 =====================
/**
 * 创建请求方法
 */
const createRequest = (service: AxiosInstance) => {
  return async function <T>(config: AxiosRequestConfig, options: RequestOptions = {}): Promise<T> {
    console.log('options == ', config, options, options.useCache)
    // 接口複雜度
    const paramsComplexity = options.paramsComplexity || 'simple'
    // 优先读取缓存
    if (options.useCache) {
      try {
        const cacheData = await getCacheData(config.url!, config.params || config.data)
        return cacheData as T
      } catch {
        // 缓存不存在则走正常请求
      }
    }

    // 处理POST/PUT的Content-Type和参数
    const requestConfig = { ...config }
    const { method, params = {} } = requestConfig

    // 處理診所編碼：將 'tcma-self-all' 轉為空字符串
    if (isObject(params) && params.clinicCode === 'tcma-self-all') {
      params.clinicCode = ''
    }

    if (['post', 'put', 'POST', 'PUT'].includes(method!)) {
      const { contentType, data } = handlePostPutConfig(method!, params, paramsComplexity)
      console.log('contentType == ', contentType)
      console.log('data == ', data)
      requestConfig.data = trimParams(data, options.trimType)
      requestConfig.headers = {
        ...requestConfig.headers,
        'Content-Type': contentType,
      }
      // 关键修复：POST/PUT 请求时，参数在 body 中，清空 params，避免重复发送到 query
      requestConfig.params = undefined
    } else {
      // GET等其他请求：参数去空格
      requestConfig.params = trimParams(params, options.trimType)
    }

    console.log('requestConfig == ', requestConfig, VITE_APP_ENV === 'development', options.baseURL)
    // 开发环境允许指定请求使用不同域名
    if (VITE_APP_ENV === 'development') {
      if (options.baseURL) {
        requestConfig.baseURL = options.baseURL
        if (requestConfig?.headers) {
          // @ts-ignore
          // requestConfig.headers.Domain = 'test.tcma.hknongs.cn'
        }
      }
    }

    // 合并基础配置
    const defaultConfig: AxiosRequestConfig & {
      cache?: boolean
      useCache?: boolean
    } = {
      timeout: options.unTimeOut ? 0 : 30000,
      responseType: options.responseType,
      cache: options.cache,
      useCache: options.useCache,
    }

    const mergeConfig = merge({}, defaultConfig, requestConfig)

    const res = await service(mergeConfig)
    // 如果設置了 hasAll，且返回結果是數組，則在首位插入 allClinicItem
    if (options.hasAll && Array.isArray(res)) {
      res.unshift(allClinicItem)
    }
    return res as T
  }
}

// ===================== 导出实例 =====================
const axiosInstance = createAxiosInstance()
export const request = createRequest(axiosInstance)

export const axiosCreateOption = {
  baseURL: BASE_API,
}
export const uploadHeaders = {
  Authorization: `Bearer ${getToken()}`,
}

// 补充国际化提示（建议移到@/locales/lang/xxx.ts）
// 示例：
// tips: {
//   non_system_interface: '非本系统的接口',
//   bad_request: '请求错误',
//   relogin: '请重新登录',
//   request_404: '请求地址404',
//   request_timeout: '请求超时',
//   system_unavailable: '系统暂不可用，请稍后重试或联系工作人员',
//   login_invalid: '登陆信息已失效，请重新登录',
//   account_other_login: '账号在其他地方登录',
//   unknown_error: '未知错误',
//   err_network: '网络异常，请检查网络连接',
//   request_error: '请求失败'
// }
